import {
  AppAvatar,
  AppBox,
  AppButton,
  AppGeneralSearch,
  AppIconButton,
  AppLink,
  AppText,
  AppTitle,
} from "../../../../commons/components";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks/app";
import { cleanUpGeneralSearchTitle, setGeneralCleanSearchField } from "../../../../redux/slices/general-search-slice";
import { User } from "../../../../commons/models/user";
import { UserStatusEnum } from "../../../../commons/enums/user-status-enum";
import { cleanUpUserList, fetchManageUserList } from "../../../../redux/slices/user/manage-user-list-slice";
import IconMultiselect from "../../../../commons/components/icons/multiselect";
import AppBorderBox from "../../../../commons/components/BorderBox";
import { toTitleCase } from "../../../../commons/utilities/string-utils";
import { ManageFootballTeam } from "../../../../commons/models/football/admin/manage-football-team";
import {
  cleanUpTeamUsersAvailable,
  fetchManageTeamUsersAvailable,
} from "../../../../redux/slices/football/admin/team/manage-team-users-available-slice";

interface UserSearchProps {
  onSelectedUsers?: (users: User[]) => void;
  withMultiSelect?: boolean;
  teamAvailableUsers?: boolean;
  team?: ManageFootballTeam;
  userSelectedUsers?: User[];
}
export default function UserSearch({
  onSelectedUsers,
  withMultiSelect,
  teamAvailableUsers,
  team,
  userSelectedUsers,
}: UserSearchProps) {
  const dispatch = useAppDispatch();
  const multiSelectRef = useRef<HTMLInputElement>(null);
  const [isMultiSelect, setMultiSelect] = useState(false);
  const [isUncheckAll, setUncheckAll] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const { userList } = useAppSelector((state) => state.manageUserList);
  const { teamUsersAvailable } = useAppSelector((state) => state.footballManageTeamUsersAvailable);
  const { generalSearchTitle } = useAppSelector((state) => state.generalSearch);

  useEffect(() => {
    dispatch(setGeneralCleanSearchField(true));
    dispatch(cleanUpGeneralSearchTitle());
  }, []);

  useEffect(() => {
    const filterParams = {
      sort: "firstName",
      name: generalSearchTitle,
      status: UserStatusEnum.Active.toString(),
      page: 0,
    };
    if (teamAvailableUsers) {
      dispatch(cleanUpTeamUsersAvailable());
      if (team && team.id) {
        dispatch(
          fetchManageTeamUsersAvailable({
            teamId: team.id,
            urlOption: { ...filterParams },
          })
        );
      }
    } else {
      dispatch(cleanUpUserList());
      dispatch(fetchManageUserList(filterParams));
    }
  }, [generalSearchTitle]);

  useEffect(() => {
    const selectedIds = new Set();
    if (userSelectedUsers && userSelectedUsers.length > 0) {
      userSelectedUsers.forEach((user) => selectedIds.add(user.id));
    }
    if (teamAvailableUsers) {
      if (teamUsersAvailable.length > 0) {
        let filteredPlayers = [];
        if (selectedIds) {
          filteredPlayers = teamUsersAvailable.all().filter((user) => !selectedIds.has(user.id));
        } else {
          filteredPlayers = teamUsersAvailable.all();
        }
        setFilteredUsers(filteredPlayers);
      } else {
        setFilteredUsers([]);
      }
    } else {
      if (userList.length > 0) {
        let filteredPlayers = [];
        if (selectedIds) {
          filteredPlayers = userList.all().filter((user) => !selectedIds.has(user.id));
        } else {
          filteredPlayers = userList.all();
        }
        setFilteredUsers(filteredPlayers);
      } else {
        setFilteredUsers([]);
      }
    }
  }, [userList, teamUsersAvailable, userSelectedUsers]);

  useEffect(() => {
    if (isUncheckAll) {
      setSelectedUsers([]);
      setUncheckAll(false);
    }
  }, [isUncheckAll]);

  const handleUserClick = (event: Event, user: User) => {
    event.preventDefault();
    if (onSelectedUsers) {
      onSelectedUsers([user]);
    }
  };

  const handleMultiSelectClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    multiSelectRef.current?.click();
  };

  const handlePlayerSelection = (e: any, user: User) => {
    if (e.target.checked) {
      setSelectedUsers([...selectedUsers, user]);
    } else {
      setSelectedUsers(selectedUsers.filter((t) => t.id !== user.id));
    }
  };

  const handleSaveUsers = () => {
    if (onSelectedUsers) {
      onSelectedUsers(selectedUsers);
    }
  };

  return (
    <AppBox flexDirection="column" gap="md">
      <AppBox flexDirection="column" gap="sm">
        <AppBox gap="xs">
          <AppBox className="w-100 flex-1">
            <AppGeneralSearch />
          </AppBox>
          {withMultiSelect && (
            <>
              <AppIconButton
                icon={<IconMultiselect />}
                variant="light"
                onClick={(e) => handleMultiSelectClick(e)}
                color={isMultiSelect ? "primary" : "gray"}
                borderLight
              />
              <input
                type="checkbox"
                hidden
                ref={multiSelectRef}
                checked={isMultiSelect}
                onChange={(e) => {
                  setMultiSelect(e.target.checked);
                  setSelectedUsers([]);
                }}
              />
            </>
          )}
        </AppBox>
        <AppBox flexDirection="column" style={{ height: "30rem", overflowY: "scroll" }}>
          <>
            {filteredUsers &&
              filteredUsers.map((user, index) => (
                <AppBorderBox border={["Bottom"]} py="xs" key={index}>
                  {isMultiSelect ? (
                    <AppBox justifyContent="space-between" alignItems="center" pr="xs" className="flex-1">
                      <AppBox justifyContent="space-between" alignItems="center" className="flex-1">
                        <AppBox gap="xs" alignItems="center">
                          <AppAvatar username={user.fullName || ""} src={user?.avatar} size="md" />
                          <AppBox flexDirection="column">
                            <AppTitle as="h6">{toTitleCase(user.fullName)}</AppTitle>
                          </AppBox>
                        </AppBox>
                      </AppBox>
                      <label className="ee-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedUsers.some((u) => u.id === user.id)}
                          onChange={(e) => {
                            handlePlayerSelection(e, user);
                          }}
                        />
                        <span className="checkmark"></span>
                      </label>
                    </AppBox>
                  ) : (
                    <AppLink onClick={(e) => handleUserClick(e, user)}>
                      <AppBox justifyContent="space-between" alignItems="center" className="flex-1">
                        <AppBox gap="xs" alignItems="center">
                          <AppAvatar username={user.fullName || ""} src={user?.avatar} size="md" />
                          <AppBox flexDirection="column">
                            <AppTitle as="h6">{toTitleCase(user.fullName)}</AppTitle>
                          </AppBox>
                        </AppBox>
                      </AppBox>
                    </AppLink>
                  )}
                </AppBorderBox>
              ))}
            {filteredUsers.length === 0 && <AppText>There are not any users</AppText>}
          </>
        </AppBox>
      </AppBox>
      {isMultiSelect && (
        <AppBox justifyContent="end" gap="xs">
          <AppButton variant="outline" borderLight label="Clear" color="gray" onClick={() => setUncheckAll(true)} />
          <AppButton label={"Add Users"} onClick={handleSaveUsers} />
        </AppBox>
      )}
    </AppBox>
  );
}
